import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

@Component({
  selector: 'tablesready-frontend-scrollbar',
  templateUrl: './frontend-scrollbar.component.html',
  styleUrls: ['./frontend-scrollbar.component.scss']
})
export class FrontendScrollbarComponent implements AfterViewInit {
  @ViewChild('scrollbarThumbHorizontal') scrollbarThumbHorizontal?: ElementRef;
  @ViewChild('scrollbarThumbVertical') scrollbarThumbVertical?: ElementRef;
  @ViewChild('scrollContent') scrollContent?: ElementRef;
  @Input() canvasAreaHeight?: string;
  @Input() canvasAreaWidth?: string;
  @Input() noZoomWidth?: number;
  @Input() showVertical = true;
  @Input() showHorizontal = true;

  scrolled$: Observable<[number, number]>;

  private readonly scrolledSource$ = new BehaviorSubject<[number, number]>([0, 0]);
  private minimumThumbSize = 30;
  private isDragging = false;
  private lastPageY = 0;
  private lastPageX= 0;

  constructor() {
    this.scrolled$ = this.scrolledSource$.asObservable();
  }

  get scrollLeft(): number {
    return this.scrollContent.nativeElement.scrollLeft;
  }

  get scrollTop(): number {
    return this.scrollContent.nativeElement.scrollTop;
  }

  get boundingBox(): DOMRect {
    return this.scrollContent.nativeElement.getBoundingClientRect();
  }

  ngAfterViewInit() {
    this.calculateThumbSize();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.calculateThumbSize();
  }

  update(viewportX: number, viewportY: number): void {
    this.calculateThumbSize();
    this.updateThumbsPosition();

    this.scrollXTo(viewportX);
    this.scrollYTo(viewportY);
  }

  scrollXTo(newPosX: number): void {
    this.scrollContent.nativeElement.scrollLeft = newPosX;
    this.updateThumbsPosition();
    this.scrolledSource$.next([newPosX, Number(this.scrollContent.nativeElement.scrollTop)]);
  }

  scrollYTo(newPosY: number): void {
    this.scrollContent.nativeElement.scrollTop = newPosY;
    this.updateThumbsPosition();
    this.scrolledSource$.next([Number(this.scrollContent.nativeElement.scrollLeft), newPosY]);
  }

  private calculateThumbSize() {
    const scrollElement = this.scrollContent.nativeElement;
    const thumbVertical = this.scrollbarThumbVertical.nativeElement;
    const thumbHorizontal = this.scrollbarThumbHorizontal.nativeElement;

    const scrollHeight = scrollElement.scrollHeight;
    const clientHeight = scrollElement.clientHeight;
    const thumbHeight = Math.max((clientHeight / scrollHeight) * clientHeight, this.minimumThumbSize);
    thumbVertical.style.height = `${thumbHeight}px`;


    const scrollWidth = scrollElement.scrollWidth;
    const clientWidth = scrollElement.clientWidth;
    const thumbWidth = Math.max((clientWidth / scrollWidth) * clientWidth, this.minimumThumbSize);
    thumbHorizontal.style.width = `${thumbWidth}px`;
  }

  onDragStart(event: any): void { // Set 'any' to make it works on old Safari browsers
    event.stopPropagation();
    event.preventDefault();
  }

  onThumbStart(event: MouseEvent | TouchEvent, isVertical = true) {
    this.isDragging = true;

    if (isVertical) {
      this.lastPageY = this.getYCoordinate(event);
      window.addEventListener('mousemove', this.onThumbMoveVertical);
      window.addEventListener('touchmove', this.onThumbMoveVertical);
    } else {
      this.lastPageX = this.getXCoordinate(event);
      window.addEventListener('mousemove', this.onThumbMoveHorizontal);
      window.addEventListener('touchmove', this.onThumbMoveHorizontal);
    }

    window.addEventListener('mouseup', this.onThumbEnd);
    window.addEventListener('touchend', this.onThumbEnd);
  }

  onThumbMoveVertical = (event: MouseEvent | TouchEvent): void => {
    if (!this.isDragging) {
      return;
    }

    const pageY = this.getYCoordinate(event);
    const deltaY = pageY - this.lastPageY;

    const scrollElement = this.scrollContent.nativeElement;
    const verticalScrollRatio = scrollElement.scrollHeight / scrollElement.clientHeight;

    this.scrollYTo(scrollElement.scrollTop + deltaY * verticalScrollRatio);
    this.updateThumbsPosition();

    this.lastPageY = pageY;
  }

  onThumbMoveHorizontal = (event: MouseEvent | TouchEvent): void => {
    if (!this.isDragging) {
      return;
    }

    const pageX = this.getXCoordinate(event);
    const deltaX = pageX - this.lastPageX;

    const scrollElement = this.scrollContent.nativeElement;
    const horizontalScrollRatio = scrollElement.scrollWidth / scrollElement.clientWidth;

    this.scrollXTo(scrollElement.scrollLeft + deltaX * horizontalScrollRatio);
    this.updateThumbsPosition();

    this.lastPageX = pageX;
  }

  onThumbEnd = (event: MouseEvent | TouchEvent): void => {
    this.isDragging = false;

    window.removeEventListener('mousemove', this.onThumbMoveVertical);
    window.removeEventListener('touchmove', this.onThumbMoveVertical);

    window.removeEventListener('mousemove', this.onThumbMoveHorizontal);
    window.removeEventListener('touchmove', this.onThumbMoveHorizontal);

    window.removeEventListener('mouseup', this.onThumbEnd);
    window.removeEventListener('touchend', this.onThumbEnd);
  }

  private getYCoordinate(event: MouseEvent | TouchEvent): number {
    return event instanceof MouseEvent ? event.pageY : event.touches[0].pageY;
  }

  private getXCoordinate(event: MouseEvent | TouchEvent): number {
    return event instanceof MouseEvent ? event.pageX : event.touches[0].pageX;
  }

  private updateThumbsPosition(): void {
    const scrollElement = this.scrollContent.nativeElement;

    const verticalPosition = (scrollElement.scrollTop / scrollElement.scrollHeight) * scrollElement.clientHeight;
    this.scrollbarThumbVertical.nativeElement.style.top = `${verticalPosition}px`;

    const horizontalPosition = (scrollElement.scrollLeft / scrollElement.scrollWidth) * scrollElement.clientWidth;
    this.scrollbarThumbHorizontal.nativeElement.style.left = `${horizontalPosition}px`;
  }
}
